import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Download } from "lucide-react";
import { cardCls, inputCls, labelCls, fmtDate } from "./ui";

const STATUS_COLORS = {
  NEW: "text-midnight", QUALIFIED: "text-blue-600", ACTIVE: "text-emerald-600", PRIORITY: "text-gold",
  DORMANT: "text-midnight/40", RESTRICTED: "text-red-600", ARCHIVED: "text-midnight/30",
};

export const ClientsSection = () => {
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () =>
    api.get("/admin/clients", { params: { search, status } }).then((r) => {
      setItems(r.data.items);
      setStatuses(r.data.statuses);
    }).catch(() => {});

  useEffect(() => { load(); }, [status]);

  const save = async () => {
    await api.patch(`/admin/clients/${selected.id}`, { status: selected.status, notes: selected.notes });
    setSelected(null);
    load();
    toast.success("Client updated");
  };

  return (
    <div data-testid="admin-clients-section">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input placeholder="Search company, contact, email…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="clients-search" className={`${inputCls} max-w-xs`} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="clients-status-filter" className={`${inputCls} w-44`}>
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="bg-midnight text-white text-xs font-medium px-5 py-2.5 hover:bg-midnight-light">Search</button>
        <a href={`${api.defaults.baseURL}/admin/clients/export/csv`} data-testid="clients-export" className="ml-auto flex items-center gap-2 text-xs font-medium text-midnight hover:text-gold">
          <Download size={13} /> Export CSV
        </a>
      </div>

      <div className={`${cardCls} overflow-x-auto`}>
        <div className="hidden lg:grid grid-cols-12 gap-3 px-5 py-3.5 border-b border-midnight/10">
          {["Company", "Contact", "Country", "Type", "Consent", "RFQs", "Status"].map((h, i) => (
            <span key={h} className={`text-[10px] font-bold uppercase tracking-[0.15em] text-midnight/40 ${["col-span-3", "col-span-3", "col-span-1", "col-span-2", "col-span-1", "col-span-1", "col-span-1"][i]}`}>{h}</span>
          ))}
        </div>
        {items.length === 0 && <p className="px-5 py-10 text-sm text-midnight/40">No clients registered yet.</p>}
        {items.map((c) => (
          <button key={c.id} onClick={() => setSelected(c)} data-testid={`client-${c.id}`} className="w-full text-left grid grid-cols-2 lg:grid-cols-12 gap-3 items-center px-5 py-4 border-b border-midnight/[0.06] hover:bg-[#FAFAFA] transition-colors">
            <span className="lg:col-span-3">
              <span className="block text-sm font-medium text-midnight">{c.company_name}</span>
              <span className="block text-xs text-midnight/45">{c.email}</span>
            </span>
            <span className="text-sm text-midnight/65 lg:col-span-3">{c.contact_name}{c.job_title ? ` — ${c.job_title}` : ""}</span>
            <span className="text-xs text-midnight/55 lg:col-span-1">{c.country}</span>
            <span className="text-xs text-midnight/55 lg:col-span-2">{c.business_type}</span>
            <span className={`text-[11px] font-bold lg:col-span-1 ${c.marketing_consent ? "text-emerald-600" : "text-midnight/30"}`}>{c.marketing_consent ? "YES" : "NO"}</span>
            <span className="text-xs text-midnight/55 lg:col-span-1">{c.rfq_count}</span>
            <span className={`text-[11px] font-bold tracking-wide lg:col-span-1 ${STATUS_COLORS[c.status] || ""}`}>{c.status}</span>
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-midnight/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="client-detail-modal">
          <div className="bg-white hairline shadow-medium max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-heading text-xl text-midnight">{selected.company_name}</h3>
                <p className="text-xs text-midnight/45 mt-1">Registered {fmtDate(selected.created_at)} · {selected.source}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-midnight/40 hover:text-midnight" aria-label="Close"><X size={18} /></button>
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Contact", `${selected.contact_name}${selected.job_title ? ` — ${selected.job_title}` : ""}`],
                ["Email", selected.email], ["Secondary Email", selected.secondary_email], ["Website", selected.website],
                ["Country", `${selected.country}${selected.city ? `, ${selected.city}` : ""}`],
                ["Reg. Number", selected.reg_number], ["VAT / Tax", selected.vat_number], ["Business Type", selected.business_type],
                ["Order Value", selected.order_value], ["Monthly Volume", selected.monthly_volume],
                ["Buying Regions", selected.buying_regions], ["Delivery Destinations", selected.delivery_destinations],
                ["Marketing Consent", selected.marketing_consent ? `Yes — ${fmtDate(selected.consent?.ts)} (${selected.consent?.version})` : "No"],
                ["RFQs", `${selected.rfq_count}${selected.last_enquiry ? ` · last ${fmtDate(selected.last_enquiry)}` : ""}`],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] font-bold uppercase tracking-wide text-midnight/40">{k}</dt>
                  <dd className="text-midnight/80 mt-0.5 break-words">{v || "—"}</dd>
                </div>
              ))}
            </dl>
            {selected.interests?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {selected.interests.map((i) => <span key={i} className="text-[11px] bg-midnight/[0.05] text-midnight/70 px-3 py-1">{i}</span>)}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-5 border-t border-midnight/10">
              <div>
                <label className={labelCls}>Status</label>
                <select value={selected.status} onChange={(e) => setSelected((s) => ({ ...s, status: e.target.value }))} data-testid="client-status-select" className={inputCls}>
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Notes</label>
                <textarea rows={2} value={selected.notes || ""} onChange={(e) => setSelected((s) => ({ ...s, notes: e.target.value }))} data-testid="client-notes" className={inputCls} />
              </div>
            </div>
            <button onClick={save} data-testid="client-save" className="mt-5 bg-midnight text-white text-sm font-semibold px-8 py-3 hover:bg-midnight-light transition-colors">Save</button>
          </div>
        </div>
      )}
    </div>
  );
};
